export const SHARED_FILES_CACHE = 'fiduly-shared-files'

// Maximum age (ms) of a cached share before we consider it stale and drop it
// without surfacing it to the form. Covers the realistic worst case — user
// shares to the PWA, then has to log in or gets distracted before the form
// mounts — without resurrecting forgotten shares from days ago.
const TTL_MS = 60 * 60 * 1000

// One-shot read: pulls every file the service worker stashed there from the
// Android share sheet, returns them as File objects, and clears the cache so
// a refresh of the form does not re-add the same receipt. Entries older than
// TTL_MS are dropped (deleted, not returned).
export async function consumeSharedFiles(
  now: () => number = Date.now,
): Promise<File[]> {
  if (typeof caches === 'undefined') return []
  let cache: Cache
  try {
    cache = await caches.open(SHARED_FILES_CACHE)
  } catch {
    return []
  }
  const requests = await cache.keys()
  const cutoff = now() - TTL_MS
  const reads = await Promise.all(
    requests.map(async (req) => {
      const res = await cache.match(req)
      if (!res) return null
      if (isExpired(res.headers.get('X-Shared-At'), cutoff)) return null
      const blob = await res.blob()
      const headerName = res.headers.get('X-Filename')
      const name = headerName ? decodeURIComponent(headerName) : 'shared'
      return new File([blob], name, { type: blob.type })
    }),
  )
  // Drop every entry — kept and expired alike. The keepers became the
  // returned File array (one-shot semantics); the expired ones we want gone
  // so they never resurface.
  await Promise.all(requests.map((req) => cache.delete(req)))
  return reads.filter((f): f is File => f !== null)
}

function isExpired(header: string | null, cutoff: number): boolean {
  // No header → entry from a build that predates this code (or non-SW writer).
  // Treat as fresh: better surface a possibly-old image once than silently
  // discard a legitimate share.
  if (!header) return false
  const sharedAt = Number(header)
  if (!Number.isFinite(sharedAt)) return true
  return sharedAt < cutoff
}
